import { useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { api } from "../../api/api";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import {
  Captcha,
  captchaRequired,
  captchaUnsupported,
  useAuthProviders,
  type CaptchaHandle,
} from "../../auth/Captcha";
import { ErrorBox, Spinner } from "../../components/ui";
import { postLoginPath } from "../../lib/nav";
import { AuthShell } from "./AuthShell";

// Google's "G" mark (inline — no external assets on the auth page).
function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

// Same convention as the local frontend's Login: "Remember me" persists the
// account email for prefill (never the password); sessions themselves are
// 7-day cookies either way.
const REMEMBER_KEY = "icc.rememberedEmail";

function readRemembered(): string {
  try {
    return localStorage.getItem(REMEMBER_KEY) ?? "";
  } catch {
    return "";
  }
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where RequireAuth was sending the visitor; validated to an in-app path.
  const from = (location.state as { from?: unknown } | null)?.from;
  const [searchParams] = useSearchParams();
  const remembered = readRemembered();
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(remembered !== "");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The Google callback lands back here with ?error=google on any failure.
  const oauthError = searchParams.get("error") === "google";

  const providers = useAuthProviders();
  const captcha = providers.data?.captcha;
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<CaptchaHandle>(null);
  const captchaPending =
    (captchaRequired(captcha) && !captchaToken) || captchaUnsupported(captcha);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api.login({
        email, password, captcha_token: captchaToken ?? undefined,
      });
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, email);
        else localStorage.removeItem(REMEMBER_KEY);
      } catch {
        /* storage unavailable — ignore */
      }
      setUser(me);
      navigate(postLoginPath(from), { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        apiErr.status === 401
          ? t("auth.invalidCredentials")
          : apiErr.status === 429
            ? t("auth.tooManyAttempts", {
                defaultValue: "Too many failed sign-in attempts. Try again in {{s}} s.",
                s: apiErr.retryAfter ?? 60,
              })
            : apiErr.message,
      );
      // The token was spent on this attempt — a retry needs a fresh one.
      captchaRef.current?.reset();
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t("auth.signIn")}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label" htmlFor="email">{t("auth.email")}</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            className="input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="password">{t("auth.password")}</label>
          <div className="relative mt-1">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              autoComplete="current-password"
              className="input !pr-9"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 px-2.5 text-muted hover:text-text transition"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              title={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            className="accent-[#018ff4] w-4 h-4"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          {t("auth.rememberMe")}
        </label>
        <Captcha ref={captchaRef} config={captcha} onToken={setCaptchaToken} />
        {error && <ErrorBox message={error} />}
        {oauthError && !error && (
          <ErrorBox message={t("auth.googleFailed", { defaultValue: "Google sign-in failed. Please try again." })} />
        )}
        <button
          type="submit"
          className="btn-primary w-full justify-center"
          disabled={busy || captchaPending}
        >
          {busy ? <Spinner /> : t("auth.signIn")}
        </button>
      </form>
      {providers.data?.google && (
        <>
          <div className="my-4 flex items-center gap-3 text-xs text-muted">
            <span className="flex-1 border-t border-border" />
            {t("auth.or", { defaultValue: "or" })}
            <span className="flex-1 border-t border-border" />
          </div>
          {/* Plain link — the server runs the whole OAuth flow and returns
              with the same session cookie password login sets. */}
          <a href="/api/auth/google/login" className="btn-ghost w-full justify-center">
            <GoogleMark />
            {t("auth.signInWithGoogle", { defaultValue: "Sign in with Google" })}
          </a>
        </>
      )}
      <div className="mt-4 flex items-center justify-between text-sm">
        <Link className="text-brand hover:underline" to="/forgot">{t("auth.forgot")}</Link>
        <Link className="text-brand hover:underline" to="/register" state={location.state}>{t("auth.signUp")}</Link>
      </div>
    </AuthShell>
  );
}
